import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/common/router';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { useDashboardFavorite } from '@/common/components/organisms/baseMenu/baseMenu.uses';
import { useInstanceStore } from '@/common/stores/instance';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { useEllipsisTooltip } from '@/common/components/molecules/simpleTextTooltip/ellipsisTooltip.uses';
import { usePaSearchSectionStore } from '@/common/stores/slide-detail';

export interface Props {
  menuInfo: MenuInfo;
  clicked?: boolean;
}

export interface Emit {
  (e: 'hover', event?: MouseEvent, menuInfo?: MenuInfo): void;
  (e: 'click', event?: MouseEvent, menuInfo?: MenuInfo): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { changeGNBInstance } = useInstanceStore();

  const baseMenuStore = useBaseMenuStore();
  const { selectSubPanelMenu } = baseMenuStore;
  const { menuVisitRoute } = storeToRefs(baseMenuStore);
  const { openSearchSection } = usePaSearchSectionStore();

  const { isFavorited, isAnalysisFavorited, updateFavorite } = useDashboardFavorite(props.menuInfo);

  const favoriteRef = ref<InstanceType<typeof BaseButton> | null>(null);
  const childLength = computed<number>(() => props.menuInfo.subMenuList?.length ?? 0);
  const isCurrentPath = computed<boolean>(() => {
    if (props.menuInfo.subMenuList) {
      return menuVisitRoute.value.some((route) => route.value === props.menuInfo.value);
    }

    // 대시보드의 경우 value 기반 비교를 먼저 수행 (dashboard_Dashboard View_{id} 형태)
    if (props.menuInfo.value.includes('dashboard_Dashboard View_')) {
      const { menuType } = router.currentRoute.value.query;
      const currentPath = router.currentRoute.value.path;

      // 경로에서 대시보드 ID 추출 (/dashboard/view/{id} 형태)
      const pathMatch = currentPath.match(/\/dashboard\/view\/([^/?]+)/);
      const dashboardId = pathMatch ? pathMatch[1] : null;

      if (!dashboardId) {
        return false;
      }

      const expectedValue = `dashboard_Dashboard View_${dashboardId}`;
      return (
        props.menuInfo.value === expectedValue &&
        (menuType === 'dashboard' || menuType === 'analysisBoard')
      );
    }

    // 일반 메뉴의 경우 경로 기반 비교
    if (props.menuInfo.path) {
      return router.currentRoute.value.path === props.menuInfo.path;
    }

    return false;
  });

  // 대시보드 항목 여부. 즐겨찾기 아이콘 노출과 라벨 줄 수(1줄 말줄임 + 툴팁) 두 정책이
  // 현재는 같은 기준을 쓰지만 서로 다른 관심사이므로, 갈리면 각각 별도 computed 으로 분리한다.
  const isDashboardMenu = computed<boolean>(() => {
    const isDashboard = props.menuInfo.value.includes(`${BASE_MENU_VALUE.DASHBOARD}_`);
    const isDashboardList = props.menuInfo.value.includes(`${BASE_MENU_VALUE.DASHBOARD}_list`);

    return isDashboard && !isDashboardList;
  });
  const isFavoriteIconVisible = computed<boolean>(() => isDashboardMenu.value);

  const { tooltipMouseEvent, onMouseEventOnText } = useEllipsisTooltip(
    () => isFavoriteIconVisible.value,
  );

  const onHover = (e?: MouseEvent) => {
    emit('hover', e, props.menuInfo);
  };

  const onClick = async (e?: MouseEvent) => {
    const favoriteEl = favoriteRef.value?.innerButtonRef;
    const targetEl = e?.target as HTMLElement;

    if (props.menuInfo.subMenuList) {
      emit('click', e, props.menuInfo);
      return;
    }

    if (!props.menuInfo.path) {
      return;
    }

    // PA 섹션 메뉴 클릭 시, 좌측 검색 슬라이드 열림
    if (props.menuInfo.meta?.category === BASE_MENU_VALUE.PA && isCurrentPath.value) {
      openSearchSection();
      return;
    }

    if (!favoriteEl) {
      const [, rootPath, secondPath] = props.menuInfo.path.split('/');
      if (secondPath === 'multiView' || secondPath === 'singleView') {
        changeGNBInstance(rootPath, secondPath);
        return;
      }
    }
    if (favoriteEl?.contains(targetEl)) {
      updateFavorite();
      return;
    }
    selectSubPanelMenu(props.menuInfo.value);
    router.push(props.menuInfo.path);
  };

  return {
    favoriteRef,

    isCurrentPath,
    isDashboardMenu,
    isFavoriteIconVisible,
    isFavorited,
    isAnalysisFavorited,

    childLength,

    tooltipMouseEvent,

    onHover,
    onClick,
    onMouseEventOnText,
  };
};
